'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { resendVerificationAction } from '../api/resendVerificationAction';

const RESEND_COOLDOWN_SECONDS = 60;

type Params = {
  email: string;
  mapApiError: (code: string) => string;
  isBusy: boolean;
};

export function useVerifyEmailResend({ email, mapApiError, isBusy }: Params) {
  const t = useTranslations('auth.verifyEmail');
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((value) => value - 1), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  async function onResend() {
    if (!email || cooldown > 0 || resending || isBusy) return;

    setResending(true);
    try {
      const result = await resendVerificationAction(email);
      if (result && !result.ok) {
        toast.error(mapApiError(result.code));
        return;
      }
      toast.success(t('resend.success'));
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      toast.error(mapApiError('server_error'));
    } finally {
      setResending(false);
    }
  }

  return {
    resending,
    cooldown,
    resendDisabled: cooldown > 0 || resending || isBusy,
    onResend,
  };
}
